/**
 * Network utility functions for offline detection and handling
 */
import React from "react";

/**
 * Check if the browser is currently online
 */
export const isOnline = (): boolean => {
  return typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
    ? navigator.onLine
    : true; // Default to online if we can't detect
};

/**
 * Add a listener for online/offline events
 */
export const addNetworkStatusListener = (
  onlineCallback: () => void,
  offlineCallback: () => void,
): (() => void) => {
  if (typeof window === "undefined") {
    return () => {}; // No-op for SSR
  }

  const handleOnline = () => {
    onlineCallback();
  };

  const handleOffline = () => {
    offlineCallback();
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  // Return a cleanup function
  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  };
};

/**
 * Create a promise that resolves when the browser comes back online
 */
export const waitForOnline = (): Promise<void> => {
  return new Promise((resolve) => {
    if (isOnline()) {
      resolve();
      return;
    }

    const handleOnline = () => {
      window.removeEventListener("online", handleOnline);
      resolve();
    };

    window.addEventListener("online", handleOnline);
  });
};

/**
 * Check if a URL is reachable
 * This is a more reliable way to check connectivity than navigator.onLine
 */
export const isUrlReachable = async (url: string, timeout = 5000): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const response = await fetch(url, {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return response.type === "opaque" || response.ok;
  } catch (error) {
    return false;
  }
};

/**
 * Custom hook for network status
 */
export const useNetworkStatus = () => {
  const [isNetworkOnline, setIsNetworkOnline] = React.useState(isOnline());

  React.useEffect(() => {
    const cleanup = addNetworkStatusListener(
      () => setIsNetworkOnline(true),
      () => setIsNetworkOnline(false),
    );

    // Check actual connectivity by pinging a reliable endpoint
    const checkActualConnectivity = async () => {
      const reachable = await isUrlReachable("https://www.google.com");
      setIsNetworkOnline(reachable);
    };

    // Check immediately and then periodically
    checkActualConnectivity();
    const intervalId = setInterval(checkActualConnectivity, 30000); // Every 30 seconds

    return () => {
      cleanup();
      clearInterval(intervalId);
    };
  }, []);

  return isNetworkOnline;
};

export default {
  isOnline,
  addNetworkStatusListener,
  waitForOnline,
  isUrlReachable,
};
